// SHARED — keep in sync across pro/care/backbar/academy (canonical: workspace/shared-web-auth/). Extract to @rootegra/auth later.
//
// The experience switcher list. Deliberately unstyled: semantic markup with
// stable `rg-switcher-*` class hooks plus per-slot classNames overrides — the
// host app owns the look (heritage tokens in pro/academy/backbar, Care design
// system in care). Memberships that live in another app render as plain <a>
// links to that app; memberships the host app can adopt in place (e.g.
// multi-salon staff switching the active salon) render as buttons via
// isSameApp/onSwitchInApp.

import type { Membership } from './memberships';
import { destinationFor, labelFor, membershipId } from './memberships';

export type SwitcherMenuClassNames = Partial<{
  root: string;
  account: string;
  accountName: string;
  accountEmail: string;
  list: string;
  item: string;
  itemCurrent: string;
  entry: string; // the <a> / <button> inside each item
  entryCurrent: string;
  signOut: string;
}>;

export type SwitcherMenuProps = {
  /** Current account header. Either line is optional. */
  accountName?: string | null;
  accountEmail?: string | null;
  /** Flattened memberships (see flattenMemberships). */
  memberships: Membership[];
  /** membershipId() of the hat the user is currently wearing — marked, not clickable-away. */
  currentId?: string | null;
  /**
   * Return true when choosing this membership should switch in place instead
   * of navigating (it targets THIS app — e.g. multi-salon staff in pro).
   */
  isSameApp?: (m: Membership) => boolean;
  /** Called when a same-app membership is chosen. */
  onSwitchInApp?: (m: Membership) => void;
  onSignOut: () => void;
  signOutLabel?: string;
  classNames?: SwitcherMenuClassNames;
};

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export function SwitcherMenu({
  accountName,
  accountEmail,
  memberships,
  currentId,
  isSameApp,
  onSwitchInApp,
  onSignOut,
  signOutLabel,
  classNames,
}: SwitcherMenuProps) {
  return (
    <div className={cx('rg-switcher', classNames?.root)}>
      {(accountName || accountEmail) && (
        <div className={cx('rg-switcher-account', classNames?.account)}>
          {accountName ? (
            <span className={cx('rg-switcher-account-name', classNames?.accountName)}>
              {accountName}
            </span>
          ) : null}
          {accountEmail ? (
            <span className={cx('rg-switcher-account-email', classNames?.accountEmail)}>
              {accountEmail}
            </span>
          ) : null}
        </div>
      )}
      <ul className={cx('rg-switcher-list', classNames?.list)}>
        {memberships.map((m) => {
          const id = membershipId(m);
          const isCurrent = currentId != null && id === currentId;
          const sameApp = isSameApp?.(m) ?? false;
          const entryClass = cx(
            'rg-switcher-entry',
            classNames?.entry,
            isCurrent && 'rg-switcher-entry--current',
            isCurrent && classNames?.entryCurrent,
          );
          return (
            <li
              key={id}
              className={cx(
                'rg-switcher-item',
                classNames?.item,
                isCurrent && classNames?.itemCurrent,
              )}
            >
              {sameApp && !isCurrent ? (
                <button type="button" className={entryClass} onClick={() => onSwitchInApp?.(m)}>
                  {labelFor(m)}
                </button>
              ) : (
                <a
                  className={entryClass}
                  href={destinationFor(m)}
                  aria-current={isCurrent ? 'true' : undefined}
                >
                  {labelFor(m)}
                </a>
              )}
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className={cx('rg-switcher-signout', classNames?.signOut)}
        onClick={onSignOut}
      >
        {signOutLabel ?? 'Sign out'}
      </button>
    </div>
  );
}
